import {inject, Injectable} from '@angular/core';
import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {catchError, switchMap} from 'rxjs/operators';
import {MsalService} from '@azure/msal-angular';
import {
    AccountInfo,
    AuthenticationResult,
    BrowserAuthError,
    BrowserAuthErrorCodes,
    InteractionRequiredAuthError
} from '@azure/msal-browser';
import {loginRequest, protectedResources} from '../../../auth.config';
import {ClientStateService} from '../client-state.service';
import {classifyShellLoadFailure} from '../shell-load-failure.util';
import {buildMsalFailureDiagnostic, MsalFailureDiagnostic} from './msal-failure-diagnostic.util';

/**
 * HTTP interceptor that adds authentication tokens to requests.
 * This interceptor uses MSAL for initial authentication and forwards
 * the token to the Shine Auth service for user/client information.
 */
@Injectable()
export class EnhancedMsalInterceptor implements HttpInterceptor {
    private msalService = inject(MsalService);
    private clientStateService = inject(ClientStateService);
    private loginInProgress = false;
    private lastLoggedFailureEpoch: number | null = null;

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Skip authentication for specific endpoints
        if (this.shouldSkipAuth(req.url)) {
            return next.handle(req);
        }

        const scopes = this.getScopesForProtectedUrl(req.url);
        if (!scopes) {
            return next.handle(req);
        }

        const account = this.resolveAccount();
        if (!account) {
            this.clientStateService.setBetaBackendUnavailable(false);
            const failureState = this.clientStateService.recordTokenAcquisitionFailure('auth-required');
            const shellFailure = failureState.failure ?? 'auth-required';
            this.clientStateService.setClientLoading(false);
            const interactiveRecoveryTriggered = this.triggerLoginRedirect(
                failureState.epoch ?? 0,
                this.msalService.instance.getAllAccounts().length > 1
            );
            this.logFailureDiagnostic(buildMsalFailureDiagnostic({
                phase: 'missing-account',
                epoch: failureState.epoch ?? 0,
                forwarded: false,
                interactiveRecoveryTriggered,
                requestUrl: req.url,
                shellFailure
            }));
            return throwError(() => new Error('No active MSAL account found'));
        }

        return this.msalService.acquireTokenSilent({scopes, account}).pipe(
            catchError(error => {
                const classifiedShellFailure = classifyShellLoadFailure(error);
                const failureState = this.clientStateService.recordTokenAcquisitionFailure(classifiedShellFailure);
                const shellFailure = failureState.failure ?? classifiedShellFailure;
                this.clientStateService.setBetaBackendUnavailable(false);
                this.clientStateService.setClientLoading(false);

                const interactiveRecoveryTriggered = this.requiresInteractiveLogin(error)
                    ? this.triggerLoginRedirect(failureState.epoch ?? 0)
                    : false;
                this.logFailureDiagnostic(buildMsalFailureDiagnostic({
                    phase: 'acquire-token-silent',
                    epoch: failureState.epoch ?? 0,
                    forwarded: false,
                    interactiveRecoveryTriggered,
                    requestUrl: req.url,
                    shellFailure,
                    error
                }));

                return throwError(() => error);
            }),
            switchMap((tokenResponse: AuthenticationResult) => {
                this.clientStateService.clearTokenAcquisitionFailure();
                this.loginInProgress = false;
                const clonedReq = req.clone({
                    setHeaders: {
                        Authorization: `Bearer ${tokenResponse.accessToken}`
                    }
                });
                return next.handle(clonedReq);
            })
        );
    }

    /**
     * Determines whether authentication should be skipped for the given URL.
     * @param url The URL to check.
     * @returns True if authentication should be skipped, false otherwise.
     */
    private shouldSkipAuth(url: string): boolean {
        // Skip authentication for health check endpoints
        if (url.endsWith('/health') || url.endsWith('/health/ready')) {
            return true;
        }

        // Skip authentication for login endpoints
        if (url.includes('/api/auth/login') || url.includes('/api/auth/refresh-token')) {
            return true;
        }

        if (this.isLocalWidgetPreviewFixtureRequest(url)) {
            return true;
        }

        return false;
    }

    private isLocalWidgetPreviewFixtureRequest(url: string): boolean {
        try {
            const parsed = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
            const isLocalHost = ['localhost', '127.0.0.1', '[::1]', '::1'].includes(parsed.hostname);
            const isFixtureRoute = parsed.pathname.endsWith('/Conversation/WidgetPreviewFixture/ProductCard');
            const clientId = parsed.searchParams.get('client');
            const chatbotId = parsed.searchParams.get('chatbot');

            return isLocalHost
                && isFixtureRoute
                && clientId === 'widget-preview-local'
                && chatbotId === 'widget-preview-product-card';
        } catch {
            return false;
        }
    }

    /**
     * Resolves the active MSAL account or promotes the only cached account.
     * @returns The AccountInfo if available, otherwise null.
     */
    private resolveAccount(): AccountInfo | null {
        const activeAccount = this.msalService.instance.getActiveAccount();
        if (activeAccount) {
            return activeAccount;
        }

        const accounts = this.msalService.instance.getAllAccounts();
        if (accounts.length === 1) {
            this.msalService.instance.setActiveAccount(accounts[0]);
            return accounts[0];
        }

        return null;
    }

    /**
     * Determines the scopes that should be used for a given request URL.
     * @param url The target URL.
     */
    private getScopesForProtectedUrl(url: string): string[] | null {
        const applicationOrigin = this.getApplicationOrigin();
        const requestUrl = this.parseRequestUrl(url, applicationOrigin);
        if (!requestUrl) {
            return null;
        }

        let matchedScopes: string[] | null = null;
        let matchedPathLength = -1;

        Object.values(protectedResources).forEach(resource => {
            if (!resource?.endpoint || !Array.isArray(resource.scopes) || resource.scopes.length === 0) {
                return;
            }

            const endpointUrl = this.parseAbsoluteUrl(resource.endpoint);
            if (!endpointUrl || endpointUrl.origin !== requestUrl.origin) {
                return;
            }

            const endpointPath = this.normalizePath(endpointUrl.pathname);
            const requestPath = this.normalizePath(requestUrl.pathname);
            if (endpointUrl.origin === applicationOrigin && endpointPath === '/') {
                return;
            }

            const matchesPath = endpointPath === '/'
                || requestPath === endpointPath
                || requestPath.startsWith(`${endpointPath}/`);

            if (matchesPath && endpointPath.length > matchedPathLength) {
                matchedScopes = resource.scopes;
                matchedPathLength = endpointPath.length;
            }
        });

        return matchedScopes;
    }

    private getApplicationOrigin(): string {
        return typeof window !== 'undefined' && window.location?.origin
            ? window.location.origin
            : 'http://localhost';
    }

    private parseRequestUrl(url: string, applicationOrigin: string): URL | null {
        try {
            return new URL(url, applicationOrigin);
        } catch {
            return null;
        }
    }

    private parseAbsoluteUrl(url: string): URL | null {
        try {
            return new URL(url);
        } catch {
            return null;
        }
    }

    private normalizePath(pathname: string): string {
        return pathname.replace(/\/+$/, '') || '/';
    }

    /**
     * Determines whether the error requires user interaction.
     * @param error The error to inspect.
     */
    private requiresInteractiveLogin(error: unknown): boolean {
        return error instanceof InteractionRequiredAuthError
            || (error instanceof BrowserAuthError && error.errorCode === BrowserAuthErrorCodes.timedOut);
    }

    /**
     * Triggers an MSAL login redirect while ensuring we only do it once per failure window.
     */
    private triggerLoginRedirect(epoch: number, promptForAccountSelection = false): boolean {
        if (this.loginInProgress || !this.clientStateService.markTokenInteractiveRecoveryStarted(epoch)) {
            return false;
        }

        this.loginInProgress = true;
        const request = promptForAccountSelection
            ? {...loginRequest, prompt: 'select_account'}
            : loginRequest;
        this.msalService.instance.loginRedirect(request).catch(() => {
            this.loginInProgress = false;
        });
        return true;
    }

    private logFailureDiagnostic(diagnostic: MsalFailureDiagnostic): void {
        if (this.lastLoggedFailureEpoch === diagnostic.epoch) {
            return;
        }

        this.lastLoggedFailureEpoch = diagnostic.epoch;
        console.warn('MSAL token acquisition diagnostic', diagnostic);
    }
}
